import React from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { VerificationBadge } from "@/components/VerificationBadge";
import { useT } from "@/lib/i18n";

const CAUSE_LABELS = {
  vocational: ["Skilling", "தொழில்"], education: ["Education", "கல்வி"], healthcare: ["Healthcare", "சுகாதாரம்"],
  agriculture: ["Agriculture", "வேளாண்மை"], water: ["Water", "நீர்"], eldercare: ["Elder Care", "முதியோர்"],
  disability: ["Disability", "மாற்றுத்திறன்"], women: ["Women", "மகளிர்"], child: ["Children", "குழந்தைகள்"],
  environment: ["Greenery", "பசுமை"], artisan: ["Artisan", "கைவினை"], disaster: ["Disaster", "பேரிடர்"],
  sanitation: ["Sanitation", "துப்புரவு"], renewable: ["Solar", "சூரிய"], animal: ["Animals", "விலங்கு"],
  waste: ["Recycling", "மறுசுழற்சி"], arts: ["Arts", "கலை"], sports: ["Sports", "விளையாட்டு"],
};

export function CauseChip({ id }) {
  const { lang } = useT();
  const label = CAUSE_LABELS[id];
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-stone-100 border border-stone-200 px-2.5 py-0.5 text-xs font-medium text-stone-700">
      {lang === "ta" ? label[1] : label[0]}
    </span>
  );
}

// The signature 5-element card. Never add a 6th element.
export function NGOCard({ org }) {
  const { t } = useT();
  return (
    <Link
      to={`/ngo/${org.id}`}
      data-testid={`ngo-card-${org.id}`}
      className="group flex flex-col bg-white rounded-xl border border-stone-200 overflow-hidden hover:border-terracotta-border hover:shadow-lg transition-[box-shadow,border-color] duration-300 focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2"
    >
      {/* 1. Photograph 16:9 */}
      <div className="relative aspect-video overflow-hidden bg-stone-100">
        <img
          src={org.hero_image}
          alt={org.display_name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <VerificationBadge tier={org.verification_tier} />
        </div>
      </div>
      <div className="flex flex-col gap-2.5 p-4">
        {/* 2. Organisation name */}
        <h3 className="text-lg font-bold text-stone-900 leading-snug line-clamp-2">{org.display_name}</h3>
        {/* 3. Cause chips (max 2) */}
        <div className="flex flex-wrap gap-1.5">
          {(org.human_cause_tags || []).slice(0, 2).map((c) => <CauseChip key={c} id={c} />)}
        </div>
        {/* 4. District */}
        <div className="flex items-center gap-1.5 text-sm text-stone-600 mt-auto">
          <MapPin size={15} className="text-terracotta shrink-0" />
          <span>{org.district}, {org.state}</span>
          {org.distance_km != null && <span className="text-stone-400">· {org.distance_km} km</span>}
        </div>
      </div>
    </Link>
  );
}

export default NGOCard;
